import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { useToast } from '../../contexts/ToastContext';
import { Card, CardContent } from '../../components/ui/card';
import { FormGroup, FormLabel, FormInput } from '../../components/ui/form';
import { PlanSelection } from '../../components/subscription';
import { ChevronLeft, ChevronRight } from 'lucide-react';

function SignUp() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const addToast = useToast();

  const handleNext = () => {
    setError(null);
    
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    } else if (step === 3 && validateStep3()) {
      handleSignUp();
    }
  };

  const validateStep1 = () => {
    if (!selectedPlan) {
      setError('Please select a plan.');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return false;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!firstName.trim()) {
      setError('Please enter your first name.');
      return false;
    }
    if (!lastName.trim()) {
      setError('Please enter your last name.');
      return false;
    }
    return true;
  };

  const handleBack = () => {
    setError(null);
    setStep(step - 1);
  };

  const handlePlanSelect = (plan, cycle) => {
    setSelectedPlan(plan);
    setBillingCycle(cycle);
  };

  const handleSignUp = async () => {
    setError(null);
    setLoading(true);

    try {
      // Sign up the user
      const { error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            selected_plan: selectedPlan.name,
            billing_cycle: billingCycle,
          }
        }
      });

      if (authError) throw authError;

      // If pro plan selected, initiate Stripe checkout (mocked for now)
      if (selectedPlan.name === 'pro') {
        // Mock Stripe integration
        console.log('Mock Stripe checkout for:', selectedPlan.name, billingCycle);
        addToast('Account created! Redirecting to payment...', 'success');
        
        // In real implementation, you would redirect to Stripe checkout
        // window.location.href = stripeCheckoutUrl;
        
        // For now, just show success and redirect
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        // Free plan - direct to home
        addToast('Account created successfully!', 'success');
        navigate('/');
      }
    } catch (error) {
      console.error('Error signing up:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <PlanSelection
              onPlanSelect={handlePlanSelect}
              selectedPlan={selectedPlan}
            />
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Create Your Account</h2>
              <p className="text-gray-600 mt-2">Enter your credentials to get started</p>
            </div>
            
            <div className="space-y-4">
              <FormGroup>
                <FormLabel htmlFor="email">Email</FormLabel>
                <FormInput
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="password">Password</FormLabel>
                <FormInput
                  id="password"
                  type="password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="confirmPassword">Confirm Password</FormLabel>
                <FormInput
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </FormGroup>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Tell Us About Yourself</h2>
              <p className="text-gray-600 mt-2">Help us personalize your experience</p>
            </div>
            
            <div className="space-y-4">
              <FormGroup>
                <FormLabel htmlFor="firstName">First Name</FormLabel>
                <FormInput
                  id="firstName"
                  type="text"
                  placeholder="Enter your first name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="lastName">Last Name</FormLabel>
                <FormInput
                  id="lastName"
                  type="text"
                  placeholder="Enter your last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </FormGroup>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className={`sm:mx-auto sm:w-full ${step === 1 ? 'sm:max-w-6xl' : 'sm:max-w-2xl'}`}>
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Welcome to Orgadoro
          </h1>
          
          {/* Step Indicator */}
          <div className="flex justify-center mt-6">
            <div className="flex space-x-2">
              {[1, 2, 3].map((stepNumber) => (
                <div
                  key={stepNumber}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    stepNumber <= step
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {stepNumber}
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex justify-center mt-2 space-x-4 text-sm text-gray-600">
            <span className={step >= 1 ? 'text-blue-600' : ''}>Plan</span>
            <span className={step >= 2 ? 'text-blue-600' : ''}>Account</span>
            <span className={step >= 3 ? 'text-blue-600' : ''}>Profile</span>
          </div>
        </div>
        
        <Card>
          <CardContent className="p-8">
            {renderStep()}
            
            {error && (
              <div className="mt-4 rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
              </div>
            )}
            
            <div className="flex justify-between mt-8">
              <button
                type="button"
                onClick={handleBack}
                disabled={step === 1}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                  step === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </button>
              
              <button
                type="button"
                onClick={handleNext}
                disabled={loading}
                className="flex items-center px-6 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {step === 3 ? 'Creating account...' : 'Processing...'}
                  </>
                ) : (
                  <>
                    {step === 3 ? 'Create Account' : 'Next'}
                    {step < 3 && <ChevronRight className="h-4 w-4 ml-1" />}
                  </>
                )}
              </button>
            </div>
            
            <div className="text-center mt-6">
              <Link 
                to="/login" 
                className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Already have an account? Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default SignUp;